import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FileText, Plus, Search, Download, Send, Eye, MoreVertical,
  CheckCircle, Clock, AlertTriangle, XCircle, IndianRupee, Edit, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { billingAPI, exportAPI } from '../lib/api';
import ExportButton from '../components/ExportButton';
import { toast } from 'sonner';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800', icon: Send },
  partially_paid: { label: 'Partially Paid', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    paid: 0,
    overdue: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalDue: 0
  });

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const { data } = await billingAPI.getInvoices(params);
      setInvoices(data.invoices || []);
      
      // Calculate stats
      const all = data.invoices || [];
      setStats({
        total: all.length,
        draft: all.filter(i => i.status === 'draft').length,
        sent: all.filter(i => i.status === 'sent').length,
        paid: all.filter(i => i.status === 'paid').length,
        overdue: all.filter(i => i.status === 'overdue').length,
        totalAmount: all.reduce((sum, i) => sum + (i.total_amount || 0), 0),
        totalPaid: all.reduce((sum, i) => sum + (i.amount_paid || 0), 0),
        totalDue: all.reduce((sum, i) => sum + (i.balance_due || 0), 0)
      });
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await billingAPI.downloadInvoicePDF(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number.replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    setSending(true);
    try {
      await billingAPI.sendInvoice(selectedInvoice.id);
      toast.success('Invoice sent successfully');
      setSendDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(searchLower) ||
      inv.customer_name?.toLowerCase().includes(searchLower) ||
      inv.project_display_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Invoices
          </h1>
          <p className="text-muted-foreground">Manage customer invoices and payments</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton 
            exportFn={exportAPI.invoices} 
            filename="invoices.csv" 
            label="Export"
          />
          <Button onClick={() => navigate('/billing/invoices/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-2xl font-bold">₹{stats.totalAmount.toLocaleString()}</p>
              </div>
              <IndianRupee className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Amount Received</p>
                <p className="text-2xl font-bold text-green-600">₹{stats.totalPaid.toLocaleString()}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">₹{stats.totalDue.toLocaleString()}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partially_paid">Partially Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 max-w-sm">
              <Input
                placeholder="Search invoices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
              <FileText className="h-16 w-16 mb-4 text-muted-foreground/30" />
              <p className="text-lg">No invoices found</p>
              <p className="text-sm">Create your first invoice to get started</p>
              <Button className="mt-4" onClick={() => navigate('/billing/invoices/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-3 font-medium">Invoice #</th>
                    <th className="p-3 font-medium">Customer</th>
                    <th className="p-3 font-medium">Project</th>
                    <th className="p-3 font-medium">Date</th>
                    <th className="p-3 font-medium">Due Date</th>
                    <th className="p-3 font-medium text-right">Amount</th>
                    <th className="p-3 font-medium text-right">Balance</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInvoices.map((inv) => {
                    const config = statusConfig[inv.status] || statusConfig.draft;
                    return (
                      <tr key={inv.id} className="hover:bg-muted/50">
                        <td className="p-3 font-mono text-sm">{inv.invoice_number}</td>
                        <td className="p-3">
                          <div className="font-medium">{inv.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{inv.customer_email}</div>
                        </td>
                        <td className="p-3 text-sm max-w-[200px] truncate">{inv.project_display_name}</td>
                        <td className="p-3 text-sm">{inv.invoice_date?.slice(0, 10)}</td>
                        <td className="p-3 text-sm">{inv.due_date?.slice(0, 10)}</td>
                        <td className="p-3 text-right font-medium">₹{inv.total_amount?.toLocaleString()}</td>
                        <td className="p-3 text-right font-medium text-orange-600">₹{inv.balance_due?.toLocaleString()}</td>
                        <td className="p-3">
                          <Badge className={config.color}>{config.label}</Badge>
                        </td>
                        <td className="p-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/billing/invoices/${inv.id}`)}>
                                <Eye className="h-4 w-4 mr-2" /> View
                              </DropdownMenuItem>
                              {(inv.status === 'draft' || inv.status === 'sent') && (
                                <DropdownMenuItem onClick={() => navigate(`/billing/invoices/${inv.id}?edit=true`)}>
                                  <Edit className="h-4 w-4 mr-2" /> Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDownloadPDF(inv)}>
                                <Download className="h-4 w-4 mr-2" /> Download PDF
                              </DropdownMenuItem>
                              {inv.status === 'draft' && (
                                <DropdownMenuItem onClick={() => { setSelectedInvoice(inv); setSendDialogOpen(true); }}>
                                  <Send className="h-4 w-4 mr-2" /> Send Invoice
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Invoice Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to send invoice <strong>{selectedInvoice?.invoice_number}</strong> to:</p>
            <ul className="mt-2 text-sm text-muted-foreground">
              <li>• {selectedInvoice?.customer_email}</li>
              {selectedInvoice?.project_sponsor_email && (
                <li>• {selectedInvoice?.project_sponsor_email}</li>
              )}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendInvoice} disabled={sending}>
              {sending ? 'Sending...' : 'Send Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
